/**
 * Modo mock do dash-web.
 *
 * Ativado por uma ÚNICA variável de ambiente: `VITE_USE_MOCKS=true`.
 * Quando ligado, instala um interceptor global de `fetch` que serve as
 * rotas `/bff/...` a partir de dados em memória — sem precisar do BFF/legacy-api.
 * Exceção: token e importação do Beefree vão ao BFF, que é quem lê a licença.
 * Ideal para subir o front estático na Vercel e o time validar todos os fluxos.
 */
import { handleMock } from './handlers';

export function isMockEnabled(): boolean {
  return import.meta.env.VITE_USE_MOCKS === 'true';
}

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

function pathOf(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.pathname + input.search;
  return input.url;
}

/** Substitui window.fetch para interceptar chamadas ao BFF no modo mock. */
export function installMockFetch(): void {
  if (typeof window === 'undefined') return;
  const realFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const raw = pathOf(input);
    // Só intercepta caminhos same-origin do BFF; o resto (imagens, QR externo…) passa direto.
    const relative = raw.replace(/^https?:\/\/[^/]+/, '');
    const pathname = relative.split('?')[0] ?? '';
    // A licença não existe no browser. Estas rotas precisam do BFF, mesmo no mock.
    const liveBeefree =
      pathname === '/bff/email/beefree-token' || pathname === '/bff/email/beefree-import';
    if (!relative.startsWith('/bff/') || liveBeefree) {
      return realFetch(input as RequestInfo, init);
    }

    const method = (init?.method ?? 'GET').toUpperCase();

    let body: unknown;
    let formData: FormData | undefined;
    if (init?.body instanceof FormData) {
      formData = init.body;
    } else if (typeof init?.body === 'string') {
      try {
        body = JSON.parse(init.body);
      } catch {
        body = undefined;
      }
    }

    await delay(140); // simula latência para os estados de carregamento aparecerem

    const { status, data, contentType } = await handleMock(method, relative, body, formData);

    if (status === 204 || data === null) {
      return new Response(null, { status });
    }
    if (contentType) {
      return new Response(String(data), { status, headers: { 'Content-Type': contentType } });
    }
    return new Response(JSON.stringify(data), {
      status,
      headers: { 'Content-Type': 'application/json' },
    });
  };

  console.info('%c[mock] dash-web rodando 100% mockado (VITE_USE_MOCKS=true)', 'color:#22c55e;font-weight:bold');
}
